// src/features/operational/components/assessment/TaskMarkList.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { useAuth } from '../../../../contexts/AuthContext';
import { 
  fetchProgrammeOfAssessment, 
  fetchTaskMarks,
  saveTaskMarks,
  fetchLearners
} from '../../services/assessmentService';

const TaskMarkList = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskMarks, setTaskMarks] = useState([]);
  const [learners, setLearners] = useState([]);
  const [filteredLearners, setFilteredLearners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [currentLearner, setCurrentLearner] = useState(null);
  const [markFormData, setMarkFormData] = useState({
    marks: '',
    absent: false,
    comments: ''
  });
  
  useEffect(() => {
    if (currentUser) {
      loadTasks();
    }
  }, [currentUser]);
  
  useEffect(() => {
    if (selectedTask) {
      loadTaskData();
    } else {
      setTaskMarks([]);
      setLearners([]);
      setFilteredLearners([]);
    }
  }, [selectedTask]);
  
  useEffect(() => {
    filterLearners();
  }, [learners, searchTerm]);
  
  const loadTasks = async () => {
    setLoading(true);
    try {
      const tasksData = await fetchProgrammeOfAssessment(currentUser.uid);
      setTasks(tasksData);
    } catch (error) {
      console.error("Error loading assessment tasks:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTaskData = async () => {
    setLoading(true);
    try {
      // Fetch task marks and learners for the selected task
      const [marksData, learnersData] = await Promise.all([
        fetchTaskMarks(selectedTask.id),
        fetchLearners(selectedTask.grade)
      ]);
      
      setTaskMarks(marksData);
      setLearners(learnersData);
    } catch (error) {
      console.error("Error loading task data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const filterLearners = () => {
    if (!learners.length) return;
    
    if (!searchTerm) {
      setFilteredLearners(learners);
      return;
    }
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = learners.filter(learner => 
      learner.name.toLowerCase().includes(searchLower)
    );
    setFilteredLearners(filtered);
  };
  
  const handleTaskChange = (event) => {
    const taskId = event.target.value;
    if (!taskId) {
      setSelectedTask(null);
      return;
    }
    
    const task = tasks.find(t => t.id === taskId);
    setSelectedTask(task);
  };
  
  const handleOpenMarkDialog = (learner) => {
    // Find existing mark for this learner
    const existingMark = taskMarks.find(mark => mark.learnerId === learner.id);
    
    if (existingMark) {
      setMarkFormData({
        marks: existingMark.mark.toString(),
        absent: existingMark.absent,
        comments: existingMark.comments || ''
      });
    } else {
      setMarkFormData({
        marks: '',
        absent: false,
        comments: ''
      });
    }
    
    setCurrentLearner(learner);
    setDialogOpen(true);
  };
  
  const handleCloseMarkDialog = () => {
    setDialogOpen(false);
    setCurrentLearner(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMarkFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSaveMark = async () => {
    if (!selectedTask || !currentLearner) return;
    
    setDialogLoading(true);
    try {
      // Prepare the mark data
      const markData = {
        taskId: selectedTask.id,
        learnerId: currentLearner.id,
        learnerName: currentLearner.name,
        mark: markFormData.absent ? 0 : parseInt(markFormData.marks),
        absent: markFormData.absent,
        comments: markFormData.comments,
        percentage: markFormData.absent ? 0 : (parseInt(markFormData.marks) / selectedTask.marks) * 100,
        weightedMark: markFormData.absent ? 0 : (parseInt(markFormData.marks) / selectedTask.marks) * selectedTask.weighting
      };
      
      // Save the mark
      await saveTaskMarks(selectedTask.id, currentLearner.id, markData);
      
      // Refresh the task data
      await loadTaskData();
      handleCloseMarkDialog();
    } catch (error) {
      console.error("Error saving mark:", error);
    } finally {
      setDialogLoading(false);
    }
  };
  
  const getLeaderboardPosition = (learnerId) => {
    if (!taskMarks.length) return '-';
    
    // Sort marks in descending order
    const sortedMarks = [...taskMarks]
      .filter(mark => !mark.absent)
      .sort((a, b) => b.mark - a.mark);
    
    // Find the position
    const position = sortedMarks.findIndex(mark => mark.learnerId === learnerId);
    
    // Return position + 1 (array is 0-indexed)
    return position !== -1 ? position + 1 : '-';
  };
  
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="medium">
          Task Mark Lists
        </Typography>
      </Box>
      
      {/* Task Selection */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Select Assessment Task</InputLabel>
          <Select
            value={selectedTask?.id || ''}
            onChange={handleTaskChange}
            label="Select Assessment Task"
          >
            <MenuItem value="">
              <em>Select a task</em>
            </MenuItem>
            {tasks.map(task => (
              <MenuItem key={task.id} value={task.id}>
                {task.title} ({task.subject.name} - Grade {task.grade})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>
      
      {selectedTask ? (
        <>
          {/* Task Details Card */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  {selectedTask.title}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                  <Chip 
                    label={`${selectedTask.subject.name} Grade ${selectedTask.grade}`} 
                    size="small" 
                    icon={<PersonIcon />}
                    sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                  <Chip 
                    label={`Term ${selectedTask.term}`} 
                    size="small"
                    sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                  <Chip 
                    label={selectedTask.taskType} 
                    size="small"
                    sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                  <Chip 
                    label={selectedTask.category} 
                    size="small"
                    sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  <strong>Date:</strong> {new Date(selectedTask.plannedDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" justifyContent="flex-end" gap={2} height="100%" alignItems="center">
                  <Card sx={{ minWidth: 120, bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="textSecondary">Total Marks</Typography>
                      <Typography variant="h6" fontWeight="bold">{selectedTask.marks}</Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ minWidth: 120, bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="textSecondary">Weighting</Typography>
                      <Typography variant="h6" fontWeight="bold">{selectedTask.weighting}%</Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Attendance Summary */}
          <Box display="flex" mb={3} gap={2} flexWrap="wrap">
            <Card sx={{ minWidth: 180, flexGrow: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Expected Attendance</Typography>
                <Typography variant="h5" fontWeight="bold">{learners.length}</Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ minWidth: 180, flexGrow: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Actual Attendance</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {learners.length - taskMarks.filter(mark => mark.absent).length}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ minWidth: 180, flexGrow: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Absentees</Typography>
                <Typography variant="h5" fontWeight="bold" color={taskMarks.filter(mark => mark.absent).length > 0 ? 'error' : 'inherit'}>
                  {taskMarks.filter(mark => mark.absent).length}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ minWidth: 180, flexGrow: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Average Score</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {taskMarks.length > 0 
                    ? `${(taskMarks.reduce((sum, mark) => sum + (mark.absent ? 0 : mark.mark), 0) / (learners.length - taskMarks.filter(mark => mark.absent).length || 1)).toFixed(1)}/${selectedTask.marks}`
                    : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          {/* Search and Filter */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Box display="flex" gap={2}>
              <TextField
                placeholder="Search learners..."
                variant="outlined"
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{
                  borderColor: THEME_COLORS.primary,
                  color: THEME_COLORS.primary,
                  '&:hover': {
                    borderColor: '#1e1c4e',
                    backgroundColor: 'rgba(39, 35, 97, 0.04)'
                  }
                }}
              >
                Export
              </Button>
            </Box>
          </Paper>
          
          {/* Mark List Table */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress sx={{ color: THEME_COLORS.primary }} />
            </Box>
          ) : filteredLearners.length === 0 ? (
            <Paper 
              elevation={1} 
              sx={{ 
                p: 4, 
                borderRadius: 2, 
                textAlign: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.02)'
              }}
            >
              <Typography variant="body1" color="textSecondary">
                No learners found for Grade {selectedTask.grade}
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, elevation: 1 }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                  <TableRow>
                    <TableCell>Learner Name</TableCell>
                    <TableCell align="center">Attendance</TableCell>
                    <TableCell align="center">Mark</TableCell>
                    <TableCell align="center">Percentage</TableCell>
                    <TableCell align="center">Weighted Mark</TableCell>
                    <TableCell align="center">Position</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLearners.map((learner) => {
                    // Find mark for this learner
                    const mark = taskMarks.find(mark => mark.learnerId === learner.id);
                    const position = mark ? getLeaderboardPosition(learner.id) : '-';
                    
                    return (
                      <TableRow key={learner.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{learner.name}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          {mark ? (
                            mark.absent ? (
                              <Chip 
                                label="Absent" 
                                size="small"
                                sx={{ 
                                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                  color: '#f44336'
                                }}
                              />
                            ) : (
                              <Chip 
                                label="Present" 
                                size="small"
                                sx={{ 
                                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                  color: '#4caf50'
                                }}
                              />
                            )
                          ) : (
                            <Chip 
                              label="Not Recorded" 
                              size="small"
                              sx={{ 
                                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                color: '#ff9800'
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {mark ? `${mark.mark}/${selectedTask.marks}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {mark ? `${mark.percentage.toFixed(1)}%` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {mark ? mark.weightedMark.toFixed(1) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {position !== '-' && (
                            <Box display="flex" justifyContent="center" alignItems="center">
                              {position <= 3 && <TrophyIcon sx={{ 
                                color: position === 1 ? '#FFD700' : position === 2 ? '#C0C0C0' : '#CD7F32',
                                mr: 0.5,
                                fontSize: '1rem'
                              }} />}
                              <Typography 
                                variant="body2" 
                                fontWeight="medium"
                                sx={{
                                  color: position <= 3 ? (
                                    position === 1 ? '#FFD700' : 
                                    position === 2 ? '#C0C0C0' : 
                                    '#CD7F32'
                                  ) : 'inherit'
                                }}
                              >
                                {position}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={mark ? <EditIcon /> : <AddIcon />}
                            onClick={() => handleOpenMarkDialog(learner)}
                            sx={{
                              borderColor: THEME_COLORS.primary,
                              color: THEME_COLORS.primary,
                              '&:hover': {
                                borderColor: '#1e1c4e',
                                backgroundColor: 'rgba(39, 35, 97, 0.04)'
                              }
                            }}
                          >
                            {mark ? 'Edit' : 'Record'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  
                  {/* Absentees (if any marked as absent but not in the filtered learners) */}
                  {taskMarks
                    .filter(mark => mark.absent)
                    .filter(mark => !filteredLearners.some(learner => learner.id === mark.learnerId))
                    .map((mark) => (
                      <TableRow key={mark.learnerId} hover sx={{ bgcolor: 'rgba(244, 67, 54, 0.05)' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{mark.learnerName}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label="Absent" 
                            size="small"
                            sx={{ 
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              color: '#f44336'
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">0/{selectedTask.marks}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">0%</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">0</Typography>
                        </TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenMarkDialog({ id: mark.learnerId, name: mark.learnerName })}
                            sx={{
                              borderColor: THEME_COLORS.primary,
                              color: THEME_COLORS.primary,
                              '&:hover': {
                                borderColor: '#1e1c4e',
                                backgroundColor: 'rgba(39, 35, 97, 0.04)'
                              }
                            }}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <Paper 
          elevation={1} 
          sx={{ 
            p: 4, 
            borderRadius: 2, 
            textAlign: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.02)'
          }}
        >
          <Typography variant="body1" color="textSecondary" mb={2}>
            Please select an assessment task to view and record marks
          </Typography>
          {tasks.length === 0 && (
            <Button
              variant="contained"
              href="#/assessment/poa"
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              Create Assessment Tasks
            </Button>
          )}
        </Paper>
      )}
      
      {/* Mark Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseMarkDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {taskMarks.find(mark => mark.learnerId === currentLearner?.id) 
            ? 'Edit Mark' 
            : 'Record Mark'}
            {currentLearner && ` - ${currentLearner.name}`}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Attendance</InputLabel>
                <Select
                  name="absent"
                  value={markFormData.absent}
                  label="Attendance"
                  onChange={(e) => setMarkFormData(prev => ({ ...prev, absent: e.target.value === 'true' }))}
                >
                  <MenuItem value="false">Present</MenuItem>
                  <MenuItem value="true">Absent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                margin="normal"
                fullWidth
                label="Mark"
                name="marks"
                type="number"
                value={markFormData.marks}
                onChange={handleInputChange}
                disabled={markFormData.absent}
                inputProps={{ 
                  min: 0, 
                  max: selectedTask?.marks || 100
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      / {selectedTask?.marks || '?'}
                    </InputAdornment>
                  ),
                }}
                helperText={
                  markFormData.absent 
                    ? "Mark set to 0 for absent learners" 
                    : `Maximum mark: ${selectedTask?.marks || 'N/A'}`
                }
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                margin="normal"
                fullWidth
                label="Comments"
                name="comments"
                value={markFormData.comments}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMarkDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveMark} 
            variant="contained"
            disabled={dialogLoading || (!markFormData.absent && !markFormData.marks)}
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            {dialogLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskMarkList;